import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    Button,
    Typography,
    IconButton,
    Collapse,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Alert,
    CircularProgress,
    Tooltip,
    Checkbox
} from '@mui/material';
import { ReactComponent as WarningImage } from '../../../assets/warning.svg';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { PaginatedResultsTable } from './PaginatedResultsTable';
import SearchField from 'components/Common/SearchField';
import PepwiseToast from 'components/Widgets/PepwiseToast';
import LoaderComponent from 'components/Loader';
import { getDecodedToken } from 'service/service';
import {
    getEwsRulesMetadata,
    getRuleConfigs,
    saveRuleConfig,
    updateRuleConfig,
    deleteRuleConfig,
    RuleConfigPayload
} from 'service/approvalWorkflow';

import {
    SectionHeader,
    StyledTextField,
    StyledSelect,
    StyledTextArea
} from './SharedComponents';
import { RuleData, OrGroupState, RuleFormState, SavedRule, RuleFormErrors, ConfiguredEwsSignal, CreateSelectRuleTabHandle } from './types';
import {
    RISK_LEVEL_OPTIONS,
    COMPONENT_OPERATOR_OPTIONS,
    METRIC_KPI_LABELS,
    DATA_SOURCE_LABELS,
    RULE_NAME_MAX_LENGTH,
    SAVE_SIMULATED_DELAY_MS,
    initialSavedRules,
    CONFIGURED_EWS_SIGNALS,
    DATAOPS_ADMIN_ROLES
} from './constants';
import {
    emptyRule,
    emptyOrGroups,
    emptyRuleForm,
    hasCompletePolicyRule,
    validateRuleForm,
    getRiskColor,
    getApprovalStatusColor,
    mapDtoToEwsSignals,
    mapDtoListToSavedRules,
    normalizeOperator,
    ensureQueryVariables,
    balanceJsonBrackets
} from './helpers';

// --- Policy Rules Components ---

interface PolicyRuleRowProps {
    rule: RuleData;
    onFieldChange: (key: keyof RuleData, value: string) => void;
    onRemove?: () => void;
    removable?: boolean;
}

const PolicyRuleRow: React.FC<PolicyRuleRowProps> = ({ rule, onFieldChange, onRemove, removable }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <StyledTextField
            label="Field/Metric Name"
            placeholder="Field/Metric Name"
            value={rule.field}
            disabled={true}
            onChange={(e) => onFieldChange('field', e.target.value)}
        />
        <Box sx={{ minWidth: 180 }}>
            <StyledSelect
                label="Component"
                value={rule.component || ''}
                disabled={true}
                placeholder=" "
                onChange={(val) => onFieldChange('component', val)}
                options={COMPONENT_OPERATOR_OPTIONS}
            />
        </Box>
        <StyledTextField
            label="Value (Threshold)"
            placeholder="Configure Value"
            value={rule.value}
            onChange={(e) => onFieldChange('value', e.target.value)}
        />
        {removable && onRemove && (
            <IconButton
                onClick={onRemove}
                sx={{ color: '#e53935' }}
            >
                <CloseIcon />
            </IconButton>
        )}
    </Box>
);

interface ORGroupCardProps {
    groupIndex: number;
    rules: RuleData[];
    expanded: boolean;
    canRemoveGroup?: boolean;
    onToggleExpand: () => void;
    onRemoveGroup?: () => void;
    onAddRule?: () => void;
    onRemoveRule?: (ruleIndex: number) => void;
    onRuleFieldChange: (ruleIndex: number, key: keyof RuleData, value: string) => void;
}

const ORGroupCard: React.FC<ORGroupCardProps> = ({
    groupIndex, rules, expanded, canRemoveGroup, onToggleExpand, onRemoveGroup, onRemoveRule, onRuleFieldChange
}) => (
    <Box sx={{
        backgroundColor: '#fff',
        borderRadius: 1,
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        mb: 2.5,
        overflow: 'hidden'
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, pb: expanded ? 0 : 2 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#333' }}>
                Rule Group #{groupIndex + 1}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {canRemoveGroup && onRemoveGroup && (
                    <Button
                        variant="contained"
                        onClick={onRemoveGroup}
                        sx={{
                            backgroundColor: '#e53935',
                            color: '#fff',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            fontSize: '0.8rem',
                            px: 2,
                            '&:hover': { backgroundColor: '#c62828' }
                        }}
                    >
                        REMOVE GROUP
                    </Button>
                )}
                <IconButton onClick={onToggleExpand} sx={{ color: '#39ACE7' }}>
                    {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
            </Box>
        </Box>

        <Collapse in={expanded}>
            <Box sx={{ p: 2, pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {rules.map((rule, ruleIndex) => (
                    <React.Fragment key={ruleIndex}>
                        {ruleIndex > 0 && (
                            <Button
                                variant="outlined"
                                size="small"
                                sx={{
                                    color: '#4caf50',
                                    borderColor: '#4caf50',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    fontSize: '0.75rem',
                                    alignSelf: 'flex-start',
                                    px: 2,
                                    mb: -0.5,
                                    mt: -0.5,
                                }}
                            >
                                AND
                            </Button>
                        )}
                        <PolicyRuleRow
                            rule={rule}
                            removable={false}
                            onFieldChange={(key, value) => onRuleFieldChange(ruleIndex, key, value)}
                            onRemove={onRemoveRule ? () => onRemoveRule(ruleIndex) : undefined}
                        />
                    </React.Fragment>
                ))}
            </Box>
        </Collapse>
    </Box>
);

const savedRuleColumnsBase = (
    onEdit: (rule: SavedRule) => void,
    onDelete: (rule: SavedRule) => void,
    isAdmin: boolean,
    selectedRuleIds: number[],
    onToggleSelectRule: (id: number) => void
) => [
    {
        name: 'SELECT',
        key: 'select',
        minWidth: 110,
        sortable: false,
        formatter: ({ row }: { row: SavedRule }) => {
            const isAlreadyMapped = Boolean(row.isMapped || (row.handler && String(row.handler).trim() && String(row.handler).trim() !== '—'));
            if (isAlreadyMapped) {
                return (
                    <Tooltip title="This rule is already mapped in Service Mapping">
                        <span>
                            <Checkbox
                                disabled={true}
                                checked={false}
                                size="small"
                                sx={{
                                    color: '#94a3b8 !important',
                                    '&.Mui-disabled': {
                                        color: '#94a3b8 !important'
                                    },
                                    p: 0.5
                                }}
                            />
                        </span>
                    </Tooltip>
                );
            }
            return (
                <Checkbox
                    checked={selectedRuleIds.includes(row.id)}
                    onChange={() => onToggleSelectRule(row.id)}
                    size="small"
                    sx={{ color: '#39ACE7', '&.Mui-checked': { color: '#39ACE7' }, p: 0.5 }}
                />
            );
        }
    },
    {
        name: 'STATUS',
        key: 'status',
        minWidth: 120,
        formatter: ({ row }: { row: SavedRule }) => {
            const status = row.status || 'ACTIVE';
            const style = getApprovalStatusColor(status);
            return (
                <Box sx={{
                    backgroundColor: style.bg,
                    color: style.color,
                    px: 1.5,
                    py: 0.3,
                    borderRadius: '12px',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    display: 'inline-block',
                    textTransform: 'uppercase'
                }}>
                    {status.replace('_', ' ')}
                </Box>
            );
        }
    },
    {
        name: 'VERSION',
        key: 'version',
        minWidth: 130,
        formatter: ({ row }: { row: SavedRule }) => (
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#003771' }}>
                {row.version || 'v1.0'}
            </Typography>
        )
    },
    {
        name: 'RISK',
        key: 'riskLevel',
        minWidth: 110,
        formatter: ({ row }: { row: SavedRule }) => (
            <Box sx={{
                backgroundColor: getRiskColor(row.riskLevel),
                color: '#fff',
                px: 1.5,
                py: 0.2,
                borderRadius: '12px',
                fontSize: '0.65rem',
                fontWeight: 700,
                display: 'inline-block',
                textTransform: 'uppercase'
            }}>
                {row.riskLevel || 'N/A'}
            </Box>
        )
    },
    { name: 'RULE NAME', key: 'ruleName', minWidth: 160 },
    { name: 'SIGNAL', key: 'ewsSignalName', minWidth: 130 },
    { name: 'METRIC', key: 'metric', minWidth: 130 },
    { name: 'DATA SOURCE', key: 'dataSourceLabel', minWidth: 160 },
    { name: 'DETECTION LOGIC', key: 'detectionLogic', minWidth: 170 },
    { name: 'NOTES', key: 'notes', minWidth: 110 },
    {
        name: 'ACTIONS',
        key: 'actions',
        minWidth: 120,
        sortable: false,
        formatter: ({ row }: { row: SavedRule }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isAdmin ? (
                    <Tooltip title="Edit Rule (Submits to approval workflow)">
                        <IconButton size="small" onClick={() => onEdit(row)} sx={{ p: 0.5 }}>
                            <EditIcon sx={{ color: '#39ACE7', fontSize: '1.2rem', cursor: 'pointer' }} />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Tooltip title="Only DataOps Admin users can edit rules">
                        <span>
                            <IconButton size="small" disabled sx={{ p: 0.5 }}>
                                <EditIcon sx={{ color: '#bdbdbd', fontSize: '1.2rem', cursor: 'not-allowed' }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                {isAdmin ? (
                    <Tooltip title="Delete Rule">
                        <IconButton size="small" onClick={() => onDelete(row)} sx={{ p: 0.5 }}>
                            <DeleteIcon sx={{ color: '#e53935', fontSize: '1.2rem', cursor: 'pointer' }} />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Tooltip title="Only DataOps Admin users can delete rules">
                        <span>
                            <IconButton size="small" disabled sx={{ p: 0.5 }}>
                                <DeleteIcon sx={{ color: '#bdbdbd', fontSize: '1.2rem', cursor: 'not-allowed' }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Box>
        )
    }
];

interface CreateSelectRuleTabProps {
    onCompletionChange?: (complete: boolean) => void;
    onSavedRulesChange?: (rules: SavedRule[]) => void;
    onRuleSelect?: (ruleId: number) => void;
    editRuleId?: number | null;
    onClearEditRuleId?: () => void;
}

const CreateSelectRuleTab = forwardRef<CreateSelectRuleTabHandle, CreateSelectRuleTabProps>(({
    onCompletionChange,
    onSavedRulesChange,
    onRuleSelect,
    editRuleId,
    onClearEditRuleId
}, ref) => {
    const [signals, setSignals] = useState<ConfiguredEwsSignal[]>(CONFIGURED_EWS_SIGNALS);
    const [isLoadingSignals, setIsLoadingSignals] = useState(false);
    const [isLoadingSavedRules, setIsLoadingSavedRules] = useState(false);

    // Filter bar
    const [metricDomain, setMetricDomain] = useState('Volume');
    const [componentFilter, setComponentFilter] = useState('');
    const [layerToolFilter, setLayerToolFilter] = useState('');

    const [ruleForm, setRuleForm] = useState<RuleFormState>(emptyRuleForm());
    const [orGroups, setOrGroups] = useState<OrGroupState[]>(emptyOrGroups());
    const [formErrors, setFormErrors] = useState<RuleFormErrors>({});
    const [touched, setTouched] = useState<{ ruleName?: boolean; ewsSignalName?: boolean }>({});

    const [savedRules, setSavedRules] = useState<SavedRule[]>(initialSavedRules);
    const [selectedRuleIds, setSelectedRuleIds] = useState<number[]>([]);
    const [savedRulesSearch, setSavedRulesSearch] = useState('');
    const [isSearchingSavedRules, setIsSearchingSavedRules] = useState(false);
    const [isClearingSavedRulesSearch, setIsClearingSavedRulesSearch] = useState(false);
    const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const [loader, setLoader] = useState<boolean>(false);

    const handleToggleSelectRule = (id: number) => {
        setSelectedRuleIds((prev) => {
            const isCurrentlySelected = prev.includes(id);
            const next = isCurrentlySelected ? prev.filter((i) => i !== id) : [id];
            onRuleSelect?.(next[0]);
            return next;
        });
    };

    const editingRule = editingRuleId != null ? savedRules.find((r) => r.id === editingRuleId) : undefined;
    const isPendingReview = editingRule?.status === 'PENDING_APPROVAL' || editingRule?.status === 'PENDING_REVIEW';

    const [ruleToDelete, setRuleToDelete] = useState<SavedRule | null>(null);
    const [existingRulesOpen, setExistingRulesOpen] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    const currentUserRoles = useMemo(() => {
        try {
            const token = getDecodedToken();
            return token?.roles || [];
        } catch {
            return [];
        }
    }, []);

    const isAdmin = useMemo(() => {
        if (!currentUserRoles.length) return true;
        return DATAOPS_ADMIN_ROLES.some((role) => currentUserRoles.includes(role));
    }, [currentUserRoles]);

    // Fetch EWS rules metadata from live backend
    useEffect(() => {
        let isMounted = true;
        const fetchSignals = async () => {
            setIsLoadingSignals(true);
            try {
                const resp = await getEwsRulesMetadata();
                const data = resp?.data || resp;
                if (isMounted && Array.isArray(data) && data.length > 0) {
                    const mapped = mapDtoToEwsSignals(data);
                    setSignals(mapped);
                }
            } catch (err) {
                console.log('EWS rules metadata backend fetch note:', err);
            } finally {
                if (isMounted) {
                    setIsLoadingSignals(false);
                }
            }
        };

        fetchSignals();
        return () => {
            isMounted = false;
        };
    }, []);

    // Fetch saved rule configurations from live backend
    useEffect(() => {
        let isMounted = true;
        const fetchSavedRules = async () => {
            setIsLoadingSavedRules(true);
            try {
                const resp = await getRuleConfigs();
                const data = resp?.data || resp;
                if (isMounted && Array.isArray(data) && data.length > 0) {
                    const mappedRules = mapDtoListToSavedRules(data);
                    setSavedRules(mappedRules);
                }
            } catch (err) {
                console.log('EWS saved rules backend fetch note:', err);
            } finally {
                if (isMounted) {
                    setIsLoadingSavedRules(false);
                }
            }
        };

        fetchSavedRules();
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        onCompletionChange?.(savedRules.length > 0);
        onSavedRulesChange?.(savedRules);
    }, [savedRules, onCompletionChange, onSavedRulesChange]);

    // When editRuleId is passed from parent (e.g. via "Change rule"), put that rule into edit mode
    useEffect(() => {
        if (editRuleId != null && savedRules.length > 0) {
            const rule = savedRules.find((r) => r.id === editRuleId);
            if (rule) {
                handleEditRule(rule);
            }
        }
    }, [editRuleId, savedRules]);

    const domainOptions = useMemo(() => {
        const set = new Set<string>();
        signals.forEach((s) => { if (s.domain) set.add(s.domain); });
        return Array.from(set).map((d) => ({ value: d, label: d }));
    }, [signals]);

    const componentOptions = useMemo(() => {
        const set = new Set<string>();
        signals.forEach((s) => { if (s.component) set.add(s.component); });
        return Array.from(set).map((c) => ({ value: c, label: c }));
    }, [signals]);

    const layerToolOptions = useMemo(() => {
        const set = new Set<string>();
        signals.forEach((s) => { if (s.layer) set.add(s.layer); });
        return Array.from(set).map((l) => ({ value: l, label: l }));
    }, [signals]);

    const filteredSignals = useMemo(() => {
        return signals.filter((s) => {
            if (metricDomain && s.domain && s.domain.toLowerCase() !== metricDomain.toLowerCase()) return false;
            if (componentFilter && s.component && s.component.toLowerCase() !== componentFilter.toLowerCase()) return false;
            if (layerToolFilter && s.layer && s.layer.toLowerCase() !== layerToolFilter.toLowerCase()) return false;
            return true;
        });
    }, [signals, metricDomain, componentFilter, layerToolFilter]);

    const signalOptions = useMemo(() => {
        const base = filteredSignals.length > 0 ? filteredSignals : signals;
        return base.map((s) => ({ value: s.signalName, label: s.signalName }));
    }, [filteredSignals, signals]);

    const metricKpiOptions = useMemo(() => {
        const set = new Set<string>();
        signals.forEach((s) => { if (s.metricKPI) set.add(s.metricKPI); });
        if (ruleForm.metricKPI) set.add(ruleForm.metricKPI);
        return Array.from(set).map((m) => ({ value: m, label: METRIC_KPI_LABELS[m] || m }));
    }, [signals, ruleForm.metricKPI]);

    const dataSourceOptions = useMemo(() => {
        const set = new Set<string>();
        signals.forEach((s) => { if (s.dataSource) set.add(s.dataSource); });
        if (ruleForm.dataSource) set.add(ruleForm.dataSource);
        return Array.from(set).map((d) => ({ value: d, label: DATA_SOURCE_LABELS[d] || d }));
    }, [signals, ruleForm.dataSource]);

    const handleSignalSelect = (signalName: string) => {
        const selectedSignal = signals.find((s) => s.signalName === signalName);
        if (!selectedSignal) return;

        setRuleForm((prev) => ({
            ...prev,
            ewsSignalName: selectedSignal.signalName,
            ruleName: selectedSignal.signalName,
            metricKPI: selectedSignal.metricKPI,
            dataSource: selectedSignal.dataSource,
            riskLevel: selectedSignal.riskLevel,
            detectionLogic: selectedSignal.detectionLogic
        }));

        if (selectedSignal.domain) setMetricDomain(selectedSignal.domain);
        if (selectedSignal.component) setComponentFilter(selectedSignal.component);
        if (selectedSignal.layer) setLayerToolFilter(selectedSignal.layer);

        const rules: RuleData[] = selectedSignal.baseVariables && selectedSignal.baseVariables.length > 0
            ? selectedSignal.baseVariables.map((v: any) => ({
                field: v.variableName || v.variableIdentifier || v.key || selectedSignal.metricLabel || 'Threshold',
                component: normalizeOperator(v.operator || (typeof v.value === 'object' ? v.value?.operator : '')) || '>',
                value: v.value != null ? (typeof v.value === 'object' ? String(v.value?.threshold ?? v.value?.value ?? '') : String(v.value)) : '',
                variableIdentifier: v.variableIdentifier || v.variableName || v.key
            }))
            : [
                {
                    field: selectedSignal.queryConfig.field,
                    component: selectedSignal.queryConfig.operator,
                    value: selectedSignal.queryConfig.defaultValue
                }
            ];

        setOrGroups([
            {
                expanded: true,
                rules
            }
        ]);

        setTouched((prev) => ({ ...prev, ewsSignalName: true }));
        setFormErrors((prev) => ({ ...prev, ewsSignalName: undefined, ruleName: undefined }));
    };

    const handleFilterChange = (filterType: 'domain' | 'component' | 'layer', val: string) => {
        if (filterType === 'domain') setMetricDomain(val);
        if (filterType === 'component') setComponentFilter(val);
        if (filterType === 'layer') setLayerToolFilter(val);
    };

    const resetForm = () => {
        setRuleForm(emptyRuleForm());
        setOrGroups(emptyOrGroups());
        setFormErrors({});
        setTouched({});
        setEditingRuleId(null);
        onClearEditRuleId?.();
    };

    const handleRuleFormChange = (key: keyof RuleFormState, value: string) => {
        setRuleForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleRuleFormBlur = (key: 'ruleName' | 'ewsSignalName') => {
        setTouched((prev) => ({ ...prev, [key]: true }));
        setFormErrors(validateRuleForm({ ...ruleForm }, savedRules, editingRuleId));
    };

    const handleOrGroupRuleFieldChange = (groupIndex: number, ruleIndex: number, key: keyof RuleData, value: string) => {
        setOrGroups((prev) => {
            const updated = prev.map((group, gi) => {
                if (gi !== groupIndex) return group;
                return {
                    ...group,
                    rules: group.rules.map((rule, ri) => (ri === ruleIndex ? { ...rule, [key]: value } : rule))
                };
            });
            return updated;
        });
    };

    const handleClearFilters = () => {
        setMetricDomain('');
        setComponentFilter('');
        setLayerToolFilter('');
        resetForm();
    };

    const handleEditRule = (rule: SavedRule) => {
        if (!isAdmin) {
            setSnackbar({ open: true, message: 'Only DataOps Admin users can edit rules.', severity: 'error' });
            return;
        }

        if (rule.domain) setMetricDomain(rule.domain);
        if (rule.component) setComponentFilter(rule.component);
        if (rule.layer) setLayerToolFilter(rule.layer);

        setRuleForm({
            ruleName: rule.ruleName,
            ewsSignalName: rule.ewsSignalName,
            metricKPI: rule.metricKPI,
            dataSource: rule.dataSourceValue || rule.dataSourceLabel,
            riskLevel: rule.riskLevel,
            detectionLogic: rule.detectionLogic,
            notes: rule.notes
        });
        setOrGroups(rule.orGroups.length ? rule.orGroups.map((g) => ({ ...g, rules: g.rules.map((r) => ({ ...r })) })) : emptyOrGroups());
        setEditingRuleId(rule.id);
        setSelectedRuleIds((prev) => prev.includes(rule.id) ? prev : [...prev, rule.id]);
        setFormErrors({});
        setTouched({});
    };

    const handleRequestDelete = (rule: SavedRule) => {
        if (!isAdmin) {
            setSnackbar({ open: true, message: 'Only DataOps Admin users can delete rules.', severity: 'error' });
            return;
        }
        setRuleToDelete(rule);
    };

    const handleConfirmDelete = async () => {
        if (!ruleToDelete) return;
        try {
            setLoader(true)
            if (ruleToDelete.backendId) {
                await deleteRuleConfig(ruleToDelete.backendId);
            }
            setRuleToDelete(null);
            setSavedRules((prev) => prev.filter((r) => r.id !== ruleToDelete.id));
            setSelectedRuleIds((prev) => prev.filter((id) => id !== ruleToDelete.id));
            if (editingRuleId === ruleToDelete.id) {
                resetForm();
            }
            setSnackbar({ open: true, message: `"${ruleToDelete.ruleName}" was deleted.`, severity: 'success' });
        } catch (err: any) {
            console.error('Error deleting rule configuration:', err);
            const msg = err?.response?.data?.message || err?.message || 'Failed to delete rule.';
            setSnackbar({ open: true, message: msg, severity: 'error' });
        } finally {
            setLoader(false)
        }
    };

    const commitRuleForm = async (isFromAutoAdvance: boolean = false): Promise<boolean> => {
        const errors = validateRuleForm(ruleForm, savedRules, editingRuleId);
        if (!hasCompletePolicyRule(orGroups)) {
            errors.policyRules = 'A valid threshold Value is required in the policy rule group.';
        }
        setFormErrors(errors);
        setTouched({ ruleName: true, ewsSignalName: true });

        if (Object.keys(errors).length > 0) {
            setSnackbar({ open: true, message: 'Please fix the highlighted errors before saving.', severity: 'error' });
            return false;
        }

        setIsSaving(true);
        try {
            const isEditing = editingRuleId != null;
            const existing = isEditing ? savedRules.find((r) => r.id === editingRuleId) : undefined;
            const currentSignalObj = signals.find((s) => s.signalName === ruleForm.ewsSignalName.trim());

            const variables = orGroups.flatMap((g) =>
                g.rules.map((r) => ({
                    variableName: r.field,
                    variableIdentifier: r.variableIdentifier || r.field.toUpperCase().replace(/\s+/g, '_'),
                    value: isNaN(Number(r.value)) || r.value === '' ? r.value : Number(r.value),
                    operator: r.component || undefined,
                    key: r.field || 'threshold'
                }))
            );

            let baseEsQueryPayload: any = null;
            let finalEsQueryPayload: any = null;
            if (currentSignalObj?.baseEsQuery) {
                const rawEs = currentSignalObj.baseEsQuery;
                let indexName = rawEs.indexName || '';
                if (typeof indexName === 'string') {
                    indexName = indexName.replace(/^'|'$/g, '').trim();
                }
                let query = rawEs.query || '';
                if (typeof query === 'string') {
                    query = balanceJsonBrackets(query.replace(/^'|'$/g, '').trim());
                }

                const updatedVariables = (currentSignalObj.baseVariables || []).map((bv, idx) => {
                    const matchingRule = orGroups[0]?.rules.find((r) =>
                        (r.variableIdentifier && r.variableIdentifier === bv.variableIdentifier) ||
                        (r.field && r.field === bv.variableName)
                    ) || orGroups[0]?.rules[idx];

                    const val = matchingRule?.value !== undefined && matchingRule?.value !== '' ? matchingRule.value : bv.value;
                    const numVal = Number(val);
                    const finalVal = val === '' || isNaN(numVal) ? val : numVal;

                    const op = matchingRule?.component ? normalizeOperator(matchingRule.component) : normalizeOperator(bv.operator);
                    let beOperator = bv.operator;
                    if (op === '>') beOperator = 'GREATER_THAN';
                    else if (op === '<') beOperator = 'LESS_THAN';
                    else if (op === '>=') beOperator = 'GREATER_THAN_OR_EQUAL';
                    else if (op === '<=') beOperator = 'LESS_THAN_OR_EQUAL';
                    else if (op === '==') beOperator = 'EQUALS';
                    else if (op === '!=') beOperator = 'NOT_EQUALS';

                    const item: any = {
                        variableName: bv.variableName || matchingRule?.field || bv.variableIdentifier,
                        variableIdentifier: bv.variableIdentifier || matchingRule?.variableIdentifier || bv.variableName,
                        value: finalVal
                    };
                    if (beOperator) item.operator = beOperator;
                    return item;
                });

                const finalVariables = updatedVariables.length > 0
                    ? updatedVariables
                    : orGroups.flatMap((g) =>
                        g.rules.map((r) => {
                            const numVal = Number(r.value);
                            return {
                                variableName: r.field,
                                variableIdentifier: r.variableIdentifier || r.field.toUpperCase().replace(/\s+/g, '_'),
                                value: isNaN(numVal) || r.value === '' ? r.value : numVal,
                                operator: r.component ? (
                                    r.component === '>' ? 'GREATER_THAN' :
                                    r.component === '<' ? 'LESS_THAN' :
                                    r.component === '>=' ? 'GREATER_THAN_OR_EQUAL' :
                                    r.component === '<=' ? 'LESS_THAN_OR_EQUAL' :
                                    r.component === '==' ? 'EQUALS' :
                                    r.component === '!=' ? 'NOT_EQUALS' : r.component
                                ) : undefined
                            };
                        })
                    );

                const resolvedVars = ensureQueryVariables(query, finalVariables, currentSignalObj?.baseVariables || []);

                baseEsQueryPayload = {
                    indexName: indexName || 'logs-pepwise_data_telemetry_metrics_v0.3.0.2025.june-pepwise_dev',
                    query,
                    variables: resolvedVars,
                    keysToExtract: rawEs.keysToExtract || []
                };

                finalEsQueryPayload = {
                    indexName: indexName || 'logs-pepwise_data_telemetry_metrics_v0.3.0.2025.june-pepwise_dev',
                    query,
                    Variables: resolvedVars.map((v: any) => ({
                        variableName: v.variableName || v.variableIdentifier || v.key || '',
                        variableIdentifier: v.variableIdentifier || v.variableName || v.key || '',
                        value: v.value,
                        ...(v.operator ? { operator: v.operator } : {})
                    })),
                    keysToExtract: rawEs.keysToExtract || []
                };
            }

            const rulePayload: RuleConfigPayload = {
                Domain: currentSignalObj?.domain || metricDomain || 'Volume',
                Component: currentSignalObj?.component || componentFilter || 'Ingestion',
                Layer_Tool: currentSignalObj?.layer || layerToolFilter || 'Bronze / All',
                Metric_KPI: ruleForm.metricKPI || 'record count',
                EWS_Signal_Name: ruleForm.ewsSignalName.trim(),
                Detection_Logic_Trend_Pattern: ruleForm.detectionLogic.trim(),
                Risk_Level: (ruleForm.riskLevel || 'MEDIUM').toUpperCase() as any,
                Data_Source: ruleForm.dataSource || 'ingestion metrics',
                Rule_Name: ruleForm.ruleName.trim(),
                Notes: ruleForm.notes.trim(),
                Schedule: {
                    Eval_Window: '14d',
                    Interval: '1h',
                    Cooldown: '30m'
                },
                Resources: [],
                Final_ES_Query: finalEsQueryPayload || {
                    indexName: 'logs-pepwise_data_telemetry_metrics_v0.3.0.2025.june-pepwise_dev',
                    query: '{"query":{"match_all":{}}}',
                    Variables: [],
                    keysToExtract: []
                },
                Variables: [],
                variables: [],
                Test_Run_Status: (existing as any)?.Test_Run_Status || (existing as any)?.testRunStatus || undefined
            };

            let backendId = isEditing ? existing?.backendId : undefined;

            if (isEditing && backendId) {
                const updateResp = await updateRuleConfig(backendId, rulePayload);
                const updatedData = updateResp?.data || updateResp;
                if (updatedData?._id) backendId = updatedData._id;
            } else {
                const createResp = await saveRuleConfig(rulePayload);
                const createdData = createResp?.data || createResp;
                if (createdData?._id) backendId = createdData._id;
            }

            const currentVer = parseFloat((existing?.version || 'v1.0').replace('v', '')) || 1.0;
            const nextVersion = isEditing ? `v${(currentVer + 0.1).toFixed(1)}` : (existing?.version || 'v1.0');

            const nextLocalId = savedRules.length ? Math.max(...savedRules.map((r) => r.id)) + 1 : 1;
            const savedRuleId = isEditing && existing ? existing.id : nextLocalId;

            const savedRule: SavedRule = {
                id: isEditing && existing?.status === 'ACTIVE'
                    ? nextLocalId
                    : savedRuleId,
                backendId,
                ruleName: ruleForm.ruleName.trim(),
                ewsSignalName: ruleForm.ewsSignalName.trim(),
                metric: METRIC_KPI_LABELS[ruleForm.metricKPI] ?? ruleForm.metricKPI,
                metricKPI: ruleForm.metricKPI,
                dataSourceLabel: DATA_SOURCE_LABELS[ruleForm.dataSource] ?? ruleForm.dataSource,
                dataSourceValue: ruleForm.dataSource,
                riskLevel: ruleForm.riskLevel,
                detectionLogic: ruleForm.detectionLogic.trim(),
                notes: ruleForm.notes.trim(),
                orGroups: orGroups.map((g) => ({ ...g, rules: g.rules.map((r) => ({ ...r })) })),
                version: isEditing && existing?.status === 'ACTIVE' ? nextVersion : (existing?.version || 'v1.0'),
                status: isEditing ? (existing?.status === 'ACTIVE' ? 'PENDING_APPROVAL' : (existing?.status || 'DRAFT')) : 'DRAFT',
                previousVersionId: isEditing && existing?.status === 'ACTIVE' ? existing.id : existing?.previousVersionId,
                domain: currentSignalObj?.domain || metricDomain || 'Volume',
                component: currentSignalObj?.component || componentFilter || 'All',
                layer: currentSignalObj?.layer || layerToolFilter || 'All',
                baseEsQuery: baseEsQueryPayload || currentSignalObj?.baseEsQuery || '',
                variables: variables as any,
                Test_Run_Status: (existing as any)?.Test_Run_Status || (existing as any)?.testRunStatus || undefined,
                testRunStatus: (existing as any)?.Test_Run_Status || (existing as any)?.testRunStatus || undefined
            };

            setSavedRules((prev) => {
                if (isEditing) {
                    const alreadyExists = prev.some((r) => r.id === savedRule.id || (r.backendId && backendId && r.backendId === backendId));
                    if (alreadyExists) {
                        return prev.map((r) => (r.id === savedRule.id || (r.backendId && backendId && r.backendId === backendId) ? savedRule : r));
                    }
                }
                setSelectedRuleIds((prevSelected) => prevSelected.includes(savedRule.id) ? prevSelected : [...prevSelected, savedRule.id]);
                return [...prev, savedRule];
            });

            setSnackbar({
                open: true,
                message: editingRuleId != null
                    ? 'Rule configuration updated successfully.'
                    : `"${savedRule.ruleName}" saved successfully.`,
                severity: 'success'
            });

            if (!isFromAutoAdvance) {
                resetForm();
            }
            return true;
        } catch (err: any) {
            console.error('Error saving rule configuration:', err);
            const msg = err?.response?.data?.message || err?.message || 'Failed to save rule configuration.';
            setSnackbar({ open: true, message: msg, severity: 'error' });
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    useImperativeHandle(ref, () => ({
        saveCurrentFormOnAdvance: async () => {
            const hasFormContent = !!ruleForm.ewsSignalName.trim() && !!ruleForm.ruleName.trim();
            if (hasFormContent) {
                return await commitRuleForm(true);
            }
            if (savedRules.length === 0) {
                setSnackbar({ open: true, message: 'Please configure and save at least one rule before proceeding.', severity: 'error' });
                return false;
            }
            return true;
        }
    }));

    const handleSave = () => {
        commitRuleForm(false);
    };

    const filteredSavedRules = savedRules.filter((rule) => {
        const query = savedRulesSearch.trim().toLowerCase();
        if (!query) return true;
        return [rule.ruleName, rule.metric, rule.dataSourceLabel, rule.detectionLogic, rule.notes]
            .some((field) => field && field.toLowerCase().includes(query));
    });

    const savedRulesColumns = useMemo(
        () => savedRuleColumnsBase(handleEditRule, handleRequestDelete, isAdmin, selectedRuleIds, handleToggleSelectRule),
        [handleEditRule, handleRequestDelete, isAdmin, selectedRuleIds]
    );

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 3, mb: 2 }}>
                <InfoIcon sx={{ color: '#a0aab4', fontSize: '1.2rem', mr: 1 }} />
                <Typography sx={{ fontStyle: 'italic', color: '#a0aab4', fontSize: '0.9rem' }}>
                    This section is to configure the rule.
                </Typography>
            </Box>

            <Box sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                mt: 2,
                mb: 3,
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    backgroundColor: '#fff',
                    p: 2,
                    borderRadius: 1,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    boxSizing: 'border-box',
                    flex: '1 1 1152px',
                    maxWidth: 1152
                }}>
                    {/* Signals field */}
                    <Box sx={{ width: '100%' }}>
                        <StyledSelect
                            label="Signals"
                            required
                            placeholder="Select Configured EWS Signal"
                            value={ruleForm.ewsSignalName}
                            onChange={handleSignalSelect}
                            options={signalOptions}
                            error={touched.ewsSignalName && !!formErrors.ewsSignalName}
                            helperText={touched.ewsSignalName ? formErrors.ewsSignalName : 'Selecting an EWS Signal auto-fills downstream metadata and policy rule thresholds'}
                        />
                    </Box>

                    {/* Filter row: Metric Domain, Component, Layer/Tool */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 3,
                        width: '100%',
                        pt: 1,
                        borderTop: '1px solid #f1f5f9'
                    }}>
                        <Box sx={{ minWidth: 200, flex: 1 }}>
                            <StyledSelect
                                label="Metric Domain"
                                value={metricDomain}
                                options={domainOptions}
                                onChange={(val) => handleFilterChange('domain', val)}
                            />
                        </Box>
                        <Box sx={{ minWidth: 200, flex: 1 }}>
                            <StyledSelect
                                label="Component"
                                value={componentFilter}
                                options={componentOptions}
                                onChange={(val) => handleFilterChange('component', val)}
                            />
                        </Box>
                        <Box sx={{ minWidth: 200, flex: 1 }}>
                            <StyledSelect
                                label="Layer/Tool"
                                value={layerToolFilter}
                                options={layerToolOptions}
                                onChange={(val) => handleFilterChange('layer', val)}
                            />
                        </Box>

                        <Button
                            variant="text"
                            startIcon={<CloseIcon sx={{ fontSize: '1rem' }}/>}
                            onClick={handleClearFilters}
                            sx={{ color: '#39ACE7', textTransform: 'uppercase', fontWeight: 600, ml: 1 }}
                        >
                            CLEAR ALL
                        </Button>
                    </Box>
                </Box>

                <Button
                    variant="outlined"
                    onClick={() => setExistingRulesOpen(true)}
                    sx={{
                        color: '#39ACE7',
                        borderColor: '#39ACE7',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        px: 3
                    }}
                >
                    VIEW EXISTING RULES
                </Button>
            </Box>

            {editingRuleId != null && (
                isPendingReview ? (
                    <Alert severity="warning" sx={{ maxWidth: 1152, mb: 2 }}>
                        Rule "{editingRule?.ruleName}" is currently <strong>Pending Approval Review</strong>. Edits cannot be saved until the review request is resolved.
                    </Alert>
                ) : (
                    <PepwiseToast
                        type="alert"
                        title="Editing Rule"
                        message={`You are modifying "${editingRule?.ruleName}". Changes will be submitted to the approval workflow; the previous approved version remains active until approved and activated.`}
                        onClose={resetForm}
                        sx={{ maxWidth: 1152, mb: 2 }}
                    />
                )
            )}

            {/* Rule Identity Section */}
            <Box sx={{ boxSizing: 'border-box', maxWidth: 1152 }}>
                <SectionHeader title="Rule Identity" />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5, mb: 2.5, boxSizing: 'border-box', width: '100%', maxWidth: 1152 }}>
                <StyledTextField
                    label="Rule Name"
                    required
                    placeholder="Enter Rule Name"
                    value={ruleForm.ruleName}
                    disabled={isPendingReview}
                    onChange={(e) => handleRuleFormChange('ruleName', e.target.value)}
                    onBlur={() => handleRuleFormBlur('ruleName')}
                    error={touched.ruleName && !!formErrors.ruleName}
                    helperText={touched.ruleName ? formErrors.ruleName : undefined}
                    inputProps={{ maxLength: RULE_NAME_MAX_LENGTH }}
                />
                <StyledSelect
                    label="Risk Level"
                    value={ruleForm.riskLevel}
                    onChange={(val) => handleRuleFormChange('riskLevel', val)}
                    options={RISK_LEVEL_OPTIONS}
                    disabled={true}
                    helperText="Auto-filled from selected EWS Signal"
                />
                <StyledSelect
                    label="Metric/KPI"
                    value={ruleForm.metricKPI}
                    onChange={(val) => handleRuleFormChange('metricKPI', val)}
                    options={metricKpiOptions}
                    disabled={true}
                    helperText="Auto-filled from selected EWS Signal"
                />
                <StyledSelect
                    label="Data Source"
                    value={ruleForm.dataSource}
                    onChange={(val) => handleRuleFormChange('dataSource', val)}
                    options={dataSourceOptions}
                    disabled={true}
                    helperText="Auto-filled from selected EWS Signal"
                />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 3, boxSizing: 'border-box', width: '100%', maxWidth: 1152 }}>
                <StyledTextArea
                    label="Detection Logic (Describe the pattern)"
                    rows={3}
                    value={ruleForm.detectionLogic}
                    onChange={(e) => handleRuleFormChange('detectionLogic', e.target.value)}
                    disabled={true}
                    helperText="Auto-filled from selected EWS Signal"
                />
                <StyledTextArea
                    label="Notes"
                    rows={3}
                    placeholder="Add optional notes or context"
                    value={ruleForm.notes}
                    disabled={isPendingReview}
                    onChange={(e) => handleRuleFormChange('notes', e.target.value)}
                />
            </Box>

            {/* Policy Rules Section */}
            <Box sx={{ boxSizing: 'border-box', maxWidth: 1152 }}>
                <SectionHeader title="Policy Rules" />
            </Box>
            <Box sx={{ boxSizing: 'border-box', width: '100%', maxWidth: 1152 }}>
                <Typography sx={{ fontStyle: 'italic', color: '#a0aab4', fontSize: '0.85rem', mb: formErrors.policyRules ? 1 : 2.5 }}>
                    Rule group is auto-created from the selected EWS Signal ES query. Modify threshold Value as needed.
                </Typography>
                {formErrors.policyRules && (
                    <Typography sx={{ color: '#d32f2f', fontSize: '0.8rem', mb: 2 }}>
                        {formErrors.policyRules}
                    </Typography>
                )}

                {orGroups.map((group, groupIndex) => (
                    <ORGroupCard
                        key={groupIndex}
                        groupIndex={groupIndex}
                        rules={group.rules}
                        expanded={group.expanded}
                        canRemoveGroup={false}
                        onToggleExpand={() => {
                            setOrGroups((prev) => prev.map((g, i) => (i === groupIndex ? { ...g, expanded: !g.expanded } : g)));
                        }}
                        onRemoveGroup={() => {
                            if (orGroups.length > 1) {
                                setOrGroups((prev) => prev.filter((_, i) => i !== groupIndex));
                            }
                        }}
                        onAddRule={() => {
                            setOrGroups((prev) => prev.map((g, i) => (i === groupIndex ? { ...g, rules: [...g.rules, emptyRule()] } : g)));
                        }}
                        onRemoveRule={(ruleIndex) => {
                            setOrGroups((prev) => prev.map((g, i) => (
                                i === groupIndex ? { ...g, rules: g.rules.filter((_, ri) => ri !== ruleIndex) } : g
                            )));
                        }}
                        onRuleFieldChange={(ruleIndex, key, value) => handleOrGroupRuleFieldChange(groupIndex, ruleIndex, key, value)}
                    />
                ))}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Button
                        variant="text"
                        onClick={resetForm}
                        disabled={isSaving}
                        sx={{
                            color: '#39ACE7',
                            fontWeight: 600,
                            textTransform: 'uppercase'
                        }}
                    >
                        CLEAR
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={isSaving || isPendingReview}
                        startIcon={isSaving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : undefined}
                        sx={{
                            backgroundColor: '#39ACE7',
                            color: '#fff',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            '&:hover': {
                                backgroundColor: '#2c8ebb'
                            },
                            '&.Mui-disabled': { backgroundColor: '#39ACE799', color: '#fff' }
                        }}
                    >
                        {isSaving ? 'SAVING...' : editingRuleId != null ? 'UPDATE RULE' : 'SAVE'}
                    </Button>
                </Box>
            </Box>

            <Box sx={{ height: '1px', backgroundColor: '#39ACE7', width: '100%', mb: 4, opacity: 0.5 }} />

            {/* Saved Rules Section */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, mt: 4 }}>
                <Typography sx={{ color: '#003771', fontWeight: 600, fontSize: '1.1rem' }}>
                    Saved Rules
                </Typography>
                <SearchField
                    value={savedRulesSearch}
                    onChange={setSavedRulesSearch}
                    onSearch={() => {
                        setIsSearchingSavedRules(true);
                        window.setTimeout(() => setIsSearchingSavedRules(false), 500);
                    }}
                    onClear={() => {
                        setIsClearingSavedRulesSearch(true);
                        setSavedRulesSearch('');
                        window.setTimeout(() => setIsClearingSavedRulesSearch(false), 500);
                    }}
                    searchIconSize={24}
                    otherIconsSize={18}
                    isSearching={isSearchingSavedRules}
                    isClearingIcon={isClearingSavedRulesSearch}
                />
            </Box>

            <Box sx={{ width: '100%', mb: 5, '.MuiDataGrid-columnHeaders': { backgroundColor: '#003771', color: '#fff' } }}>
                {
                    loader ?  
                    <LoaderComponent size={32} /> 
                    : 
                     <PaginatedResultsTable
                        tableType="ews-saved-rules"
                        columns={savedRulesColumns}
                        data={filteredSavedRules}
                        isFetching={isLoadingSavedRules}
                    />
                }
            </Box>
            {/* View Existing Rules dialog */}
            <Dialog open={existingRulesOpen} onClose={() => setExistingRulesOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600, color: '#003771' }}>Existing Rules</DialogTitle>
                <DialogContent dividers>
                    {isLoadingSavedRules ? (
                        <Box sx={{ py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <LoaderComponent size={32} />
                        </Box>
                    ) : savedRules.length === 0 ? (
                        <Typography sx={{ color: '#a0aab4', fontStyle: 'italic', py: 2, textAlign: 'center' }}>
                            No rules have been created yet.
                        </Typography>
                    ) : (
                        savedRules.map((rule) => (
                            <Box
                                key={rule.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    py: 1.25,
                                    borderBottom: '1px solid #eee'
                                }}
                            >
                                <Box>
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{rule.ruleName}</Typography>
                                    <Typography sx={{ fontSize: '0.8rem', color: '#6b7280' }}>{rule.detectionLogic || 'No detection logic provided'}</Typography>
                                </Box>
                                <Box sx={{
                                    backgroundColor: getRiskColor(rule.riskLevel),
                                    color: '#fff',
                                    px: 1.5,
                                    py: 0.2,
                                    borderRadius: '12px',
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    flexShrink: 0,
                                    ml: 2
                                }}>
                                    {rule.riskLevel || 'N/A'}
                                </Box>
                            </Box>
                        ))
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExistingRulesOpen(false)} sx={{ color: '#39ACE7', fontWeight: 600 }}>
                        CLOSE
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete confirmation dialog */}
            <Dialog
                open={!!ruleToDelete}
                onClose={() => setRuleToDelete(null)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: '#FFFFFF',
                        color: '#1e293b',
                        width: '560px !important',
                        maxWidth: '100%',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)'
                    }
                }}
            >
                {/* Header */}
                <DialogTitle
                    sx={{
                        bgcolor: 'primary.main',
                        color: '#FFFFFF',
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#FFFFFF' }}>
                        Deleting Rule
                    </Typography>
                    <IconButton onClick={() => setRuleToDelete(null)} sx={{ color: '#FFFFFF', p: 0.5 }}>
                        <CloseIcon sx={{ fontSize: '20px' }} />
                    </IconButton>
                </DialogTitle>

                {/* Content */}
                <DialogContent sx={{ padding: '24px !important', backgroundColor: '#FFFFFF' }}>
                    <Box display="flex" alignItems="center" gap={3}>
                        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <WarningImage style={{ width: 56, height: 52 }} />
                        </Box>
                        <Box textAlign="left">
                            <Typography sx={{ fontSize: '20px', fontWeight: 600, color: '#1e293b', mb: 1, lineHeight: 1.3 }}>
                                Are you sure to delete this Rule?
                            </Typography>
                            <Typography sx={{ fontSize: '14px', color: '#64748b', fontWeight: 400 }}>
                                Once confirmed, the action can't be undone.
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>

                {/* Actions */}
                <DialogActions sx={{ justifyContent: 'flex-end', padding: '16px 28px 24px 28px', gap: 2, backgroundColor: '#FFFFFF' }}>
                    <Button
                        onClick={() => setRuleToDelete(null)}
                        sx={{
                            color: '#009BD8',
                            fontWeight: 700,
                            fontSize: '14px',
                            textTransform: 'uppercase',
                            padding: '8px 16px',
                            '&:hover': { backgroundColor: 'rgba(0, 155, 216, 0.08)' }
                        }}
                    >
                        NO, I WANT IT!
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        sx={{
                            backgroundColor: '#FF2D2D !important',
                            color: '#FFFFFF !important',
                            fontWeight: 700,
                            fontSize: '14px',
                            textTransform: 'uppercase',
                            borderRadius: '4px',
                            padding: '8px 28px',
                            minWidth: '110px',
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: '#E02424 !important', boxShadow: 'none' }
                        }}
                    >
                        DELETE
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
});

export default CreateSelectRuleTab;
